"use client";
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Button,
  FormLabel,
  HStack,
  Icon,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Accept, useDropzone } from "react-dropzone";
import { FiUploadCloud } from "react-icons/fi";
import { FileListView, FileSingeView, FileThumbnailView } from "./FilePreviews";

type FileInputVariant = "thumbnail" | "list";

export type FileType = "image" | "pdf";

export interface FileInputImage {
  preview: string;
  title: string;
  file?: File;
}

export interface FileInputProps {
  maxFiles?: number;
  isMultiFile?: boolean;
  fileType?: FileType;
  variant?: FileInputVariant;
  label?: string;
  dropzoneHeading?: string;
  dropzoneSubTitle?: string;
  fluid?: boolean;
  isDropzoneAlwaysVisible?: boolean;
  isDisabled?: boolean;
  maxSize?: number;
  initialValues?: FileInputImage[];
  onChange?: (files: FileInputImage[]) => void;
  onRemoveAll?: () => void;
  value?: string | number | readonly string[] | undefined;
}

export function FileInput({
  isMultiFile,
  maxFiles,
  label,
  dropzoneHeading,
  dropzoneSubTitle,
  fluid = true,
  variant,
  isDropzoneAlwaysVisible = false,
  fileType,
  isDisabled,
  maxSize,
  onChange,
  initialValues = [],
  onRemoveAll,
  value,
}: FileInputProps) {
  const [acceptedFiles, setAcceptedFiles] =
    useState<FileInputImage[]>(initialValues);

  useEffect(() => {
    if (!value) setAcceptedFiles(initialValues);
  }, [value, initialValues]);

  const fileAcceptValidation = useMemo(() => {
    const acceptValidation: Accept = {};

    switch (fileType) {
      case "image":
        acceptValidation["image/*"] = [];
        break;
      default:
        break;
    }

    return acceptValidation;
  }, [fileType]);

  const onDrop = useCallback(
    (acceptedFileList: File[]) => {
      acceptedFileList.forEach((file: File) => {
        const reader = new FileReader();

        reader.onabort = () => console.log("file reading was aborted");
        reader.onerror = () => console.log("file reading has failed");

        reader.onload = () => {
          // Do whatever you want with the file contents
          const binaryStr = reader.result as string;

          const newFileInput = {
            preview: binaryStr,
            title: file.name,
            file,
          };
          if (isMultiFile) {
            setAcceptedFiles((prev) => {
              const newValue = [...prev, newFileInput];
              onChange?.(newValue);
              return newValue;
            });
          } else {
            onChange?.([newFileInput]);
            setAcceptedFiles([newFileInput]);
          }
        };

        reader.readAsDataURL(file);
      });
    },
    [isMultiFile, onChange],
  );

  // useEffect(() => {
  //   if (onChange) {
  //     onChange(acceptedFiles)
  //   }
  // }, [acceptedFiles])

  const { getRootProps, getInputProps, inputRef } = useDropzone({
    onDrop,
    maxFiles,
    maxSize,
    accept: fileAcceptValidation,
    disabled: isDisabled,
  });

  const PreviewRenderElement = useMemo(() => {
    if (!isMultiFile) return FileSingeView;
    switch (variant) {
      case "thumbnail":
        return FileThumbnailView;
      case "list":
        return FileListView;
      default:
        return FileThumbnailView;
    }
  }, [variant, isMultiFile]);

  return (
    <>
      <FormLabel>{label}</FormLabel>
      <input {...getInputProps<any>()} />
      {(acceptedFiles?.length === 0 || isDropzoneAlwaysVisible) && (
        <VStack
          {...getRootProps()}
          sx={{
            maxW: fluid ? "full" : "max-content",
            border: "1.5px dashed",
            borderColor: "#CBD2DC",
            borderRadius: "11px",
            backgroundColor: "#FAFBFC",
            alignItems: "center",
            justifyContent: "center",
            px: "1rem",
            py: "1.75rem",
            mb: "1rem",
            cursor: "pointer",
            gap: "6px",
            transition: "border-color 0.15s, background-color 0.15s",
            _hover: {
              borderColor: "#3A4BD8",
              backgroundColor: "#F5F6FE",
            },
          }}
        >
          <VStack
            sx={{
              w: "42px",
              h: "42px",
              borderRadius: "11px",
              bg: "#F0F1FE",
              color: "#3A4BD8",
              alignItems: "center",
              justifyContent: "center",
              mb: "2px",
            }}
          >
            <Icon as={FiUploadCloud} fontSize="xl" color="#3A4BD8" />
          </VStack>
          <Text fontSize="13px" fontWeight={500} color="#3A4150">
            {dropzoneHeading ?? "Drop files or"}{" "}
            <Text as="span" color="#3A4BD8">
              browse
            </Text>
          </Text>
          {dropzoneSubTitle && (
            <Text fontSize="11px" color="#9AA2AE">
              {dropzoneSubTitle}
            </Text>
          )}
        </VStack>
      )}

      <PreviewRenderElement
        isFluid={true}
        imageFiles={acceptedFiles}
        onRemove={(data) => {
          const newValue = acceptedFiles.filter(
            (i) => i.preview !== data.preview,
          );

          onChange?.(newValue);
          setAcceptedFiles(newValue);
        }}
      />

      {isMultiFile && acceptedFiles?.length > 0 && (
        <HStack mt="1rem" justifyContent="flex-end">
          <Button
            size="sm"
            variant="outline"
            colorScheme="surface"
            onClick={() => {
              onRemoveAll?.();
              setAcceptedFiles([]);
            }}
          >
            Remove All
          </Button>
          <Button
            size="sm"
            onClick={() => {
              inputRef.current?.click();
            }}
            leftIcon={<Icon as={FiUploadCloud} aria-label="upload-again" />}
          >
            Upload
          </Button>
        </HStack>
      )}
    </>
  );
}
